import React, { useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

import { usePasswordRequirement } from '../hooks/usePasswordRequirement';

import CreatePasswordModalV2 from './Modals/CreatePasswordModalV2';

import setUserPassword from '../api/users/setUserPassword';

import { JWTDecodedToken } from '../types/auth';

export interface PasswordRequirementGateProps {
	token: string;
	children: React.ReactNode;
}

/** El id de la persona del token, o `null` si el token no decodifica o no lo trae. */
const idDelToken = (token: string): string | null => {
	try {
		return jwtDecode<JWTDecodedToken>(token).id ?? null;
	} catch {
		return null;
	}
};

/**
 * Pide contraseña a quien tiene NIF y no la tiene, en CUALQUIER carga de la app con sesión.
 * Envuelve todas las rutas privadas (`PrivateRoute`), así que cubre también la recarga con
 * la sesión ya abierta, que antes no pasaba por ningún punto que lo mirase.
 *
 * La app se pinta siempre debajo, sin esperar a la respuesta: un backend lento no retrasa a
 * nadie y, si la consulta falla, se entra igual. Si hay que pedirla, el modal va encima y es
 * bloqueante: sin X, sin Escape y sin cierre al pinchar fuera (`CreatePasswordModalV2`).
 */
const PasswordRequirementGate = ({ token, children }: PasswordRequirementGateProps) => {
	const { required, markSatisfied } = usePasswordRequirement(token);
	// El éxito queda registrado como un HECHO, no se deduce de que el modal se cierre.
	const passwordEstablished = useRef(false);
	const userId = idDelToken(token);

	// Un token nuevo es otra sesión: la contraseña establecida con el anterior no cuenta.
	useEffect(() => {
		passwordEstablished.current = false;
	}, [token]);

	const establecerPassword = async (id: string, password: string) => {
		await setUserPassword(token, id, password);
		passwordEstablished.current = true;
	};

	/**
	 * 🔴 El modal solo se quita si la contraseña se estableció DE VERDAD. Es el fallo de la
	 * fase 1: cualquier vía de cierre de Radix concedía la sesión sin contraseña. Mismo
	 * patrón que `LoginFormV2` (`handlePasswordModalDone`).
	 */
	const handleOpenChange = (open: boolean) => {
		if (open || !passwordEstablished.current) return;

		markSatisfied();
	};

	return (
		<>
			{children}
			{required && userId !== null && (
				<CreatePasswordModalV2
					open
					onOpenChange={handleOpenChange}
					onCreatePassword={(password) => establecerPassword(userId, password)}
				/>
			)}
		</>
	);
};

export default PasswordRequirementGate;
